import { useQuery } from '@apollo/client/react';
import GET_USERS_FOR_ACCOUNT_Q from '../gql/getUsersForAccount.gql';

import { Table } from '@repo/ui/Table';
import UsersTableFilter from './UsersTableFilter';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import { z } from 'zod';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';
import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Form } from '@repo/ui/Form';
import { useUser } from '@repo/hooks/useUser';
import { POLICIES } from '@/lib/Authorization';
import { Link } from 'react-router';

const USERS_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  first_name: z.string().default(''),
  last_name: z.string().default(''),
  email: z.string().default(''),
});

const PAGE_SIZE = 10;

export interface UsersTableProps {
  accountId?: string;
}

const UsersTable = ({ accountId }: UsersTableProps) => {

  const user = useUser();
  const filter = useZodQuery({
    schema: USERS_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, ...queryFields } = filter.queryValues;
  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
      },
      coerceValue,
      ['first_name', 'last_name', 'email']
    ),
  ];

  const { loading, error, data } = useQuery(GET_USERS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    console.error(error);
  }

  const { edges: userNodes, pageInfo } = data?.usersForAccount ?? {};
  return (
    <div>
      <Form {...filter} variant="horizontal" className={'mb-6'}>
        <UsersTableFilter />
      </Form>
    
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={userNodes?.map((user) => user.node) ?? []}
        dataType="users"
        error={!!error}
        columns={[
          {
            title: 'Name',
            width: 'xl',
            Cell({ entry: { first_name, last_name, name, id, email, account } }) {
              return (
                <div className="flex flex-col">
                  {!first_name && !last_name ? (
                    POLICIES['user:update'](user) ? (
                      <Link to={`/a/${account.id}/users/${id}`} className="underline">
                        {name}
                      </Link>
                    ) : (
                      <span>{name}</span>
                    )
                  ) : POLICIES['user:update'](user) ? (
                    <Link to={`/a/${account.id}/users/${id}`} className="underline">
                      {first_name} {last_name}
                    </Link>
                  ) : (
                    <span>
                      {first_name} {last_name}
                    </span>
                  )}

                  <span className="text-sm dark:text-grey-400 text-grey-700">{email}</span>
                </div>
              );
            },
          },
          {
            title: 'Account',
            Cell({ entry: { account } }) {
              return (
                <Link to={`/a/${account.id}`} className="underline">
                  {account.name}
                </Link>
              );
            },
          },
          {
            title: 'Role',
            Cell({ entry: { role } }) {
              return <span>{role?.name}</span>;
            },
          },
        ]}
      />
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}
    </div>
  );
};

export default UsersTable;
